/**
 * `POST /gateway/api/assist/rovo/v1/pins`. Mirrors the `rovo-spaces` fetch-file convention (see
 * `useCreateSpace.fetch.ts`): `credentials: 'include'` so the existing authenticated session
 * cookie is sent — this endpoint is never called anonymously.
 */
import { performHttpFetch } from '@atlassian/conversation-assistant-utils/httpUtils';

import type { PinType } from '../types';

import type { Pin, PinsFetchContext } from './listPinsFetch';

const PINS_API_PATH = '/gateway/api/assist/rovo/v1/pins';

// Fixed per rovo-for-you/for-you-panel's own convention for this surface — see e.g.
// `useInsightsStatus`'s `experienceId: 'ai-mate'` call in for-you-panel/index.tsx.
const EXPERIENCE_ID = 'ai-mate';

/** Payload for creating a pin — a search result the user chose to pin. */
export type CreatePinRequest = {
	ari: string;
	url: string;
	pinType: PinType;
	name: string;
};

export type CreatePinFetchContext = PinsFetchContext & {
	request: CreatePinRequest;
};

// Unlike listPinsFetch's `{data: Pin[]}` envelope, POST returns the created Pin directly
// (HTTP 201, body = Pin) — confirmed against the backend's hydration PR branch. `name` is
// always null in this response; it's only resolved on GET.
export const createPinFetch = ({
	request,
	cloudId,
	product,
}: CreatePinFetchContext): Promise<Pin> =>
	performHttpFetch<Pin>({
		path: PINS_API_PATH,
		options: {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'x-cloudid': cloudId,
				'x-product': product ?? '',
				'x-experience-id': EXPERIENCE_ID,
			},
			credentials: 'include',
			body: JSON.stringify(request),
		},
	});
